import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import apiClient from '../../api/client';
import { Layers, Table2, Atom, ChevronRight, ChevronLeft, Check, AlertTriangle, Loader2, Copy, Flag, Trash2 } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DedupDialogProps {
  isOpen: boolean;
  onClose: () => void;
  fileId: string;
  fileName: string;
  columns: string[];
  onComplete?: () => void;
  /** When set, dedup runs across all files in the project instead of a single file */
  projectId?: number;
}

type Step = 'method' | 'precision' | 'preview' | 'confirm';
type Method = 'structure' | 'columns' | 'both';
type StructureLevel = 'exact' | 'no_stereo' | 'no_salts' | 'no_tautomers';
type Strategy = 'keep_first' | 'merge' | 'flag_only';

interface ClusterRow {
  row_id: number;
  row_index: number;
  smiles: string;
  data: Record<string, any>;
}

interface Cluster {
  cluster_id: number;
  match_key: string;
  match_method: string;
  rows: ClusterRow[];
  differences: Record<string, any[]>;
  identical_columns: string[];
  action?: Strategy | 'skip';
}

interface PreviewSummary {
  total_rows: number;
  total_clusters: number;
  total_duplicate_rows: number;
  unique_rows: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const DedupDialog: React.FC<DedupDialogProps> = ({
  isOpen,
  onClose,
  fileId,
  fileName,
  columns,
  onComplete,
  projectId,
}) => {
  // Wizard state
  const [step, setStep] = useState<Step>('method');
  const [method, setMethod] = useState<Method>('structure');
  const [structureLevel, setStructureLevel] = useState<StructureLevel>('exact');
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [bulkStrategy, setBulkStrategy] = useState<Strategy>('keep_first');
  const [createBackup, setCreateBackup] = useState(true);

  // Data state
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [summary, setSummary] = useState<PreviewSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedCluster, setExpandedCluster] = useState<number | null>(null);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setStep('method');
      setMethod('structure');
      setStructureLevel('exact');
      setSelectedColumns([]);
      setBulkStrategy('keep_first');
      setCreateBackup(true);
      setClusters([]);
      setSummary(null);
      setError(null);
      setExpandedCluster(null);
    }
  }, [isOpen]);

  // Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Column toggle for "columns" and "both" methods
  const toggleColumn = useCallback((col: string) => {
    setSelectedColumns((prev) =>
      prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col]
    );
  }, []);

  // Run preview analysis
  const runPreview = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiClient.post('/deduplication/preview', {
        ...(projectId ? { project_id: projectId } : { file_id: fileId }),
        method,
        structure_level: structureLevel,
        columns: selectedColumns,
        similarity_threshold: 1.0,
      });
      const data = res.data;
      // Assign default action to each cluster
      const withActions = (data.clusters || []).map((c: Cluster) => ({
        ...c,
        action: bulkStrategy,
      }));
      setClusters(withActions);
      setSummary(data.summary);
      setStep('preview');
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Preview failed');
    } finally {
      setIsLoading(false);
    }
  }, [fileId, projectId, method, structureLevel, selectedColumns, bulkStrategy]);

  // Apply bulk strategy to all clusters
  const applyBulkStrategy = useCallback(
    (strategy: Strategy) => {
      setBulkStrategy(strategy);
      setClusters((prev) => prev.map((c) => ({ ...c, action: strategy })));
    },
    []
  );

  // Execute deduplication
  const executeDedup = useCallback(async () => {
    setIsExecuting(true);
    setError(null);
    try {
      // Only send clusters that aren't skipped
      const activeClusters = clusters.filter((c) => c.action !== 'skip');
      const res = await apiClient.post('/deduplication/execute', {
        ...(projectId ? { project_id: projectId } : { file_id: fileId }),
        strategy: bulkStrategy,
        clusters: activeClusters,
        create_backup: projectId ? false : createBackup,
      });
      onComplete?.();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Execution failed');
    } finally {
      setIsExecuting(false);
    }
  }, [clusters, fileId, projectId, bulkStrategy, createBackup, onComplete, onClose]);

  // Navigate steps
  const goNext = useCallback(() => {
    if (step === 'method') {
      if (method === 'columns' || method === 'both') {
        // Need column selection — stay on method if none selected
        if (selectedColumns.length === 0 && method === 'columns') {
          setError('Select at least one column');
          return;
        }
      }
      if (method === 'structure' || method === 'both') {
        setStep('precision');
      } else {
        // Columns-only: skip precision, go straight to preview
        runPreview();
      }
    } else if (step === 'precision') {
      runPreview();
    } else if (step === 'preview') {
      setStep('confirm');
    }
  }, [step, method, selectedColumns, runPreview]);

  const goBack = useCallback(() => {
    setError(null);
    if (step === 'precision') setStep('method');
    else if (step === 'preview') {
      if (method === 'columns') setStep('method');
      else setStep('precision');
    } else if (step === 'confirm') setStep('preview');
  }, [step, method]);

  if (!isOpen) return null;

  const rowsToRemove =
    bulkStrategy === 'flag_only'
      ? 0
      : clusters
          .filter((c) => c.action !== 'skip')
          .reduce((sum, c) => sum + c.rows.length - 1, 0);

  return createPortal(
    <div className="fixed inset-0 z-[1200] flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Dialog */}
      <div
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Clean Up Data</h2>
            <p className="text-sm text-gray-500 mt-0.5 truncate max-w-md">{fileName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 px-6 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium">
          {(['method', 'precision', 'preview', 'confirm'] as Step[]).map((s, i) => {
            const labels = ['Method', 'Precision', 'Preview', 'Confirm'];
            const isActive = s === step;
            const isPast =
              ['method', 'precision', 'preview', 'confirm'].indexOf(s) <
              ['method', 'precision', 'preview', 'confirm'].indexOf(step);
            return (
              <React.Fragment key={s}>
                {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-gray-300" />}
                <span
                  className={`px-2.5 py-1 rounded-full ${
                    isActive
                      ? 'bg-emerald-100 text-emerald-700'
                      : isPast
                      ? 'bg-emerald-50 text-emerald-600'
                      : 'text-gray-400'
                  }`}
                >
                  {labels[i]}
                </span>
              </React.Fragment>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Error banner */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* STEP 1: Method Selection */}
          {step === 'method' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">How should duplicates be detected?</p>

              <div className="grid grid-cols-3 gap-3">
                {/* Structure Match */}
                <button
                  onClick={() => setMethod('structure')}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    method === 'structure'
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <Atom className={`w-6 h-6 mb-2 ${method === 'structure' ? 'text-emerald-600' : 'text-gray-400'}`} />
                  <div className="font-medium text-gray-900 text-sm">Structure Match</div>
                  <p className="text-xs text-gray-500 mt-1">
                    Find molecules that are chemically identical
                  </p>
                </button>

                {/* Column Match */}
                <button
                  onClick={() => setMethod('columns')}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    method === 'columns'
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <Table2 className={`w-6 h-6 mb-2 ${method === 'columns' ? 'text-emerald-600' : 'text-gray-400'}`} />
                  <div className="font-medium text-gray-900 text-sm">Column Match</div>
                  <p className="text-xs text-gray-500 mt-1">
                    Find rows where selected columns have identical values
                  </p>
                </button>

                {/* Structure + Column */}
                <button
                  onClick={() => setMethod('both')}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    method === 'both'
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <Layers className={`w-6 h-6 mb-2 ${method === 'both' ? 'text-emerald-600' : 'text-gray-400'}`} />
                  <div className="font-medium text-gray-900 text-sm">Structure + Column</div>
                  <p className="text-xs text-gray-500 mt-1">
                    Match on both structure AND column values
                  </p>
                </button>
              </div>

              {/* Column picker for columns / both */}
              {(method === 'columns' || method === 'both') && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select columns to match on
                  </label>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                    {columns.length === 0 ? (
                      <p className="text-sm text-gray-400 p-2">No columns available</p>
                    ) : (
                      columns.map((col) => (
                        <label
                          key={col}
                          className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedColumns.includes(col)}
                            onChange={() => toggleColumn(col)}
                            className="rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
                          />
                          <span className="text-sm text-gray-700">{col}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 2: Precision */}
          {step === 'precision' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">How strict should matching be?</p>

              <div className="space-y-3">
                {(
                  [
                    {
                      value: 'exact' as StructureLevel,
                      label: 'Exact structure (stereochemistry matters)',
                      desc: 'R-Ibuprofen \u2260 S-Ibuprofen, salts kept separate',
                    },
                    {
                      value: 'no_stereo' as StructureLevel,
                      label: 'Ignore stereochemistry',
                      desc: 'R-Ibuprofen = S-Ibuprofen',
                    },
                    {
                      value: 'no_salts' as StructureLevel,
                      label: 'Ignore salts & stereochemistry',
                      desc: 'Ibuprofen sodium = Ibuprofen = S-Ibuprofen',
                    },
                    {
                      value: 'no_tautomers' as StructureLevel,
                      label: 'Broadest matching (tautomers, salts, stereo all ignored)',
                      desc: 'Keto form = Enol form',
                    },
                  ] as const
                ).map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      structureLevel === opt.value
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="precision"
                      checked={structureLevel === opt.value}
                      onChange={() => setStructureLevel(opt.value)}
                      className="mt-0.5 w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{opt.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* STEP 3: Preview */}
          {step === 'preview' && (
            <div className="space-y-4">
              {/* Summary */}
              {summary && (
                <div className="flex items-center gap-6 p-3 bg-gray-50 rounded-lg text-sm">
                  <div>
                    <span className="font-semibold text-gray-900">{summary.total_clusters}</span>
                    <span className="text-gray-500"> duplicate clusters</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900">{summary.total_duplicate_rows}</span>
                    <span className="text-gray-500"> rows involved</span>
                  </div>
                  <div>
                    <span className="text-gray-500">out of </span>
                    <span className="font-semibold text-gray-900">{summary.total_rows}</span>
                    <span className="text-gray-500"> total</span>
                  </div>
                </div>
              )}

              {/* Bulk action bar */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-gray-500 uppercase">Resolve all:</span>
                <button
                  onClick={() => applyBulkStrategy('keep_first')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                    bulkStrategy === 'keep_first'
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Keep best in each cluster
                </button>
                <button
                  onClick={() => applyBulkStrategy('merge')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                    bulkStrategy === 'merge'
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Copy className="w-3.5 h-3.5" />
                  Merge all clusters
                </button>
                <button
                  onClick={() => applyBulkStrategy('flag_only')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                    bulkStrategy === 'flag_only'
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Flag className="w-3.5 h-3.5" />
                  Flag only (add cluster ID column)
                </button>
              </div>

              {/* Cluster list */}
              {clusters.length === 0 ? (
                <div className="py-12 text-center text-gray-400">
                  <Check className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
                  <p className="text-sm font-medium text-gray-600">No duplicates found</p>
                  <p className="text-xs text-gray-400 mt-1">This file has no duplicate entries at the selected precision.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                  {clusters.map((cluster) => (
                    <div
                      key={cluster.cluster_id}
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      {/* Cluster header */}
                      <button
                        onClick={() =>
                          setExpandedCluster(
                            expandedCluster === cluster.cluster_id ? null : cluster.cluster_id
                          )
                        }
                        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                      >
                        <ChevronRight
                          className={`w-4 h-4 text-gray-400 transition-transform ${
                            expandedCluster === cluster.cluster_id ? 'rotate-90' : ''
                          }`}
                        />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-900">
                            Cluster {cluster.cluster_id}
                          </span>
                          <span className="text-xs text-gray-500 ml-2">
                            {cluster.rows.length} rows
                          </span>
                          {Object.keys(cluster.differences).length > 0 && (
                            <span className="text-xs text-amber-600 ml-2">
                              {Object.keys(cluster.differences).length} column{Object.keys(cluster.differences).length !== 1 ? 's' : ''} differ
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <select
                            value={cluster.action || bulkStrategy}
                            onChange={(e) => {
                              e.stopPropagation();
                              setClusters((prev) =>
                                prev.map((c) =>
                                  c.cluster_id === cluster.cluster_id
                                    ? { ...c, action: e.target.value as Strategy | 'skip' }
                                    : c
                                )
                              );
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs border border-gray-200 rounded-md px-2 py-1 bg-white focus:ring-1 focus:ring-emerald-500"
                          >
                            <option value="keep_first">Keep best</option>
                            <option value="merge">Merge</option>
                            <option value="flag_only">Flag only</option>
                            <option value="skip">Skip</option>
                          </select>
                        </div>
                      </button>

                      {/* Expanded detail */}
                      {expandedCluster === cluster.cluster_id && (
                        <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
                          <div className="text-xs text-gray-500 mb-2">
                            Match: <code className="bg-gray-200 px-1 rounded">{cluster.match_key.substring(0, 60)}{cluster.match_key.length > 60 ? '...' : ''}</code>
                          </div>

                          {/* Rows table */}
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b border-gray-200">
                                  <th className="text-left py-1.5 px-2 font-medium text-gray-500">Row</th>
                                  {(cluster.rows[0] as any)?.source_file != null && (
                                    <th className="text-left py-1.5 px-2 font-medium text-blue-600">Source File</th>
                                  )}
                                  <th className="text-left py-1.5 px-2 font-medium text-gray-500">Canonical SMILES</th>
                                  {cluster.rows.some((r: any) => r.input_smiles && r.input_smiles !== r.smiles) && (
                                    <th className="text-left py-1.5 px-2 font-medium text-blue-500">Original SMILES</th>
                                  )}
                                  {Object.keys(cluster.differences).map((col) => (
                                    <th
                                      key={col}
                                      className="text-left py-1.5 px-2 font-medium text-amber-600"
                                    >
                                      {col}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {cluster.rows.map((row, idx) => (
                                  <tr
                                    key={row.row_id}
                                    className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                  >
                                    <td className="py-1.5 px-2 text-gray-600">{row.row_index}</td>
                                    {(cluster.rows[0] as any)?.source_file != null && (
                                      <td className="py-1.5 px-2 text-blue-600 max-w-[140px] truncate">{(row as any).source_file || '-'}</td>
                                    )}
                                    <td className="py-1.5 px-2 text-gray-700 font-mono max-w-[200px] truncate" title={row.smiles}>
                                      {row.smiles || '-'}
                                    </td>
                                    {cluster.rows.some((r: any) => r.input_smiles && r.input_smiles !== r.smiles) && (
                                      <td className="py-1.5 px-2 text-blue-600 font-mono max-w-[200px] truncate" title={(row as any).input_smiles}>
                                        {(row as any).input_smiles || '-'}
                                      </td>
                                    )}
                                    {Object.keys(cluster.differences).map((col) => {
                                      const val = row.data?.[col];
                                      return (
                                        <td key={col} className="py-1.5 px-2">
                                          <span
                                            className={
                                              val != null && String(val).trim() !== ''
                                                ? 'text-gray-900'
                                                : 'text-gray-300 italic'
                                            }
                                          >
                                            {val != null && String(val).trim() !== '' ? String(val) : 'empty'}
                                          </span>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {cluster.identical_columns.length > 0 && (
                            <p className="mt-2 text-xs text-gray-400">
                              Identical in: {cluster.identical_columns.slice(0, 5).join(', ')}
                              {cluster.identical_columns.length > 5 &&
                                ` +${cluster.identical_columns.length - 5} more`}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* STEP 4: Confirm */}
          {step === 'confirm' && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                <div className="text-sm text-gray-700">
                  <span className="font-semibold">{clusters.filter((c) => c.action !== 'skip').length}</span> clusters will be processed
                </div>
                {bulkStrategy !== 'flag_only' && (
                  <div className="text-sm text-gray-700">
                    <span className="font-semibold text-red-600">{rowsToRemove}</span> rows will be removed
                  </div>
                )}
                {bulkStrategy === 'flag_only' && (
                  <div className="text-sm text-gray-700">
                    A <code className="bg-gray-200 px-1 rounded text-xs">DUPLICATE_CLUSTER</code> column will be added
                  </div>
                )}
                {bulkStrategy === 'merge' && (
                  <div className="text-sm text-gray-700">
                    Conflicting values will get <code className="bg-gray-200 px-1 rounded text-xs">_v1</code>, <code className="bg-gray-200 px-1 rounded text-xs">_v2</code> suffixed columns
                  </div>
                )}
              </div>

              {!projectId && (
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={createBackup}
                    onChange={(e) => setCreateBackup(e.target.checked)}
                    className="rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700">Create backup of original file</span>
                </label>
              )}

              {clusters.length === 0 && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
                  No duplicates found. Nothing to do.
                </div>
              )}
            </div>
          )}

          {/* Loading overlay */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
              <span className="ml-3 text-sm text-gray-500">Analysing structures...</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={step === 'method' ? onClose : goBack}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            disabled={isExecuting}
          >
            {step === 'method' ? 'Cancel' : 'Back'}
          </button>

          <div className="flex items-center gap-2">
            {step === 'confirm' ? (
              <button
                onClick={executeDedup}
                disabled={isExecuting || clusters.length === 0}
                className="px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {isExecuting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Applying...
                  </>
                ) : (
                  'Apply Changes'
                )}
              </button>
            ) : (
              <button
                onClick={goNext}
                disabled={isLoading}
                className="px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Analysing...
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DedupDialog;
